import { create } from 'zustand';
import { omit } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { countTextLength } from './tts';

const convertBlocksSaveToStore = (blocks) => {
  // Ensure each block has an ID
  const blocksWithIds = blocks.map((block) => ({
    ...block,
    text: block.text || '',
    id: block.id || uuidv4(),
  }));

  return {
    blockIds: blocksWithIds.map((block) => block.id),
    originalBlockIds: blocksWithIds.map((block) => block.id),
    blocks: blocksWithIds.reduce((acc, block) => {
      acc[block.id] = block;
      return acc;
    }, {}),
    originalBlocks: blocksWithIds.reduce((acc, block) => {
      acc[block.id] = block;
      return acc;
    }, {}),
  };
};

const countTextLengthBlocks = (blocks) => {
  const totalLength = Object.keys(blocks).reduce(
    (acc, blockId) => acc + countTextLength(blocks[blockId]?.text || ''),
    0,
  );
  return totalLength;
};

const useProjectDetailStore = create((set) => ({
  projectId: '',
  multiBlock: {
    blocks: {},
    blockIds: [],
  },
  title: '',
  createdAt: '',
  product: '',
  isPlaying: false,
  currentBlockId: '',
  audioLink: '',
  voiceDefault: {},
  listVoice: [],
  isDraft: false,
  duration: 0,
  currentTime: 0,
  updateDuration: (duration) => set({ duration }),
  updateCurrentTime: (currentTime) => set({ currentTime }),
  setIsDraft: (isDraft) => set({ isDraft }),
  setProjectId: (projectId) => set({ projectId }),
  setMultiBlock: (multiBlock) => set({ multiBlock }),
  setTitle: (title) => set({ title }),
  updateTitle: (title) => set({ title, isDraft: true }),
  setListVoice: (listVoice) => set({ listVoice }),
  setCreatedAt: (createdAt) => set({ createdAt }),
  setProduct: (product) => set({ product }),
  setIsPlaying: (isPlaying) => set({ isPlaying }),
  setCurrentBlockId: (currentBlockId) => set({ currentBlockId }),
  setVoiceDefault: (voiceDefault) => set({ voiceDefault }),
  setAudioLink: (audioLink) => set({ audioLink }),
  addBlock: (blockId, newBlocks) =>
    set((state) => {
      const blocksWithIds = newBlocks.map((block) => ({
        ...block,
        id: block.id || uuidv4(),
      }));

      const newBlockIds = [...state.multiBlock.blockIds];
      const index = newBlockIds.indexOf(blockId);
      if (index === -1) {
        newBlockIds.push(...blocksWithIds.map((b) => b.id));
      } else {
        newBlockIds.splice(index + 1, 0, ...blocksWithIds.map((b) => b.id));
      }
      return {
        isDraft: true,
        multiBlock: {
          ...state.multiBlock,
          blockIds: newBlockIds,
          blocks: {
            ...state.multiBlock.blocks,
            ...blocksWithIds.reduce((acc, b) => {
              acc[b.id] = b;
              return acc;
            }, {}),
          },
        },
      };
    }),
  updateBlock: (blockId, block) =>
    set((state) => ({
      isDraft: true,
      multiBlock: {
        ...state.multiBlock,
        blocks: {
          ...state.multiBlock.blocks,
          [blockId]: {
            ...state.multiBlock.blocks[blockId],
            ...block,
            audioLink: null,
            status: null,
          },
        },
      },
    })),
  // synthesisInfo: { audioLink, status, requestId }
  updateBlockSynthesisInfo: (blockId, synthesisInfo) =>
    set((state) => ({
      isDraft: true,
      multiBlock: {
        ...state.multiBlock,
        blocks: {
          ...state.multiBlock.blocks,
          [blockId]: { ...state.multiBlock.blocks[blockId], ...synthesisInfo },
        },
      },
    })),
  updateBlockElements: (blockId, elements) =>
    set((state) => ({
      multiBlock: {
        ...state.multiBlock,
        blocks: {
          ...state.multiBlock.blocks,
          [blockId]: { ...state.multiBlock.blocks[blockId], elements },
        },
      },
    })),
  updateOrderBlock: (blockIds) =>
    set((state) => ({
      isDraft: true,
      multiBlock: {
        ...state.multiBlock,
        blockIds,
      },
    })),
  deleteBlock: (blockId) =>
    set((state) => ({
      isDraft: true,
      multiBlock: {
        ...state.multiBlock,
        blockIds: state.multiBlock.blockIds.filter((id) => id !== blockId),
        blocks: omit(state.multiBlock.blocks, [blockId]),
      },
    })),
}));

const isBlockChanged = (block, originalBlock) => {
  const newBlocks = JSON.stringify(block);
  const newOriginalBlocks = JSON.stringify(originalBlock);

  if (newBlocks !== newOriginalBlocks) {
    return true;
  }
  return false;
};

const getBlockMap = (blocks) =>
  Object.keys(blocks)
    .map((blockId) => ({
      id: blockId,
      text: blocks[blockId].text,
      voiceCode: blocks[blockId].voiceCode,
      speed: blocks[blockId].speed,
    }))
    .reduce((acc, block) => {
      acc[block.id] = block;
      return acc;
    }, {});

const checkBlocksChanged = ({
  blocks,
  originalBlocks,
  blockIds,
  originalBlockIds,
}) => {
  if (!blocks || !originalBlocks) return false;
  const newBlockIds = JSON.stringify(blockIds);
  const newOriginalBlockIds = JSON.stringify(originalBlockIds);
  if (newBlockIds !== newOriginalBlockIds) return true;
  const blockMap = getBlockMap(blocks);
  const originalBlockMap = getBlockMap(originalBlocks);
  return isBlockChanged(blockMap, originalBlockMap);
};

const audioExpired = (time) => {
  if (!time) return false;
  return moment().isAfter(time);
};

const getBlockChanged = (blockWithText, originalBlocks) =>
  blockWithText.filter((block) => {
    const currentBlock = {
      text: block?.text,
      voiceCode: block?.voiceCode,
      speed: block?.speed,
    };
    const originalBlock = {
      text: originalBlocks[block.id]?.text,
      voiceCode: originalBlocks[block.id]?.voiceCode,
      speed: originalBlocks[block.id]?.speed,
    };
    return isBlockChanged(currentBlock, originalBlock) || !block?.audioLink;
  });

/**
 * Remove audioExpiredAt from blocks to reset expired status
 * @param {Array} blockIds - Array of block IDs
 * @param {Object} blocks - Object containing all blocks
 * @returns {Array} - Array of blocks with Audio link
 */
const getBlockWithAudio = (blockIds, blocks) =>
  blockIds
    .filter((id) => blocks[id]?.audioLink)
    .map((id) => {
      const block = blocks[id];
      if (block.audioExpiredAt) {
        return {
          ...block,
          audioExpiredAt: null,
        };
      }
      return block;
    });

export {
  convertBlocksSaveToStore,
  countTextLengthBlocks,
  useProjectDetailStore,
  checkBlocksChanged,
  isBlockChanged,
  audioExpired,
  getBlockChanged,
  getBlockWithAudio,
};
